import { BaseRestRepository } from '~/repositories/baseRestRepository';
import { envConfig } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import type {
  ConnectedAccountDto,
  ConnectedAccountDtoPaginationDto,
  CreateAchPaymentMethodRequestDto,
  CreateCardPaymentMethodRequestDto,
  CreatePaymentMethodRequestDto,
  CreateTokenPaymentMethodRequestDto,
  DeletePaymentMethodRequestDto,
  ExchangeAuthorizedMethodsRequestDto,
  ExchangedAuthorizedMethodsDto,
  GetPaymentMethodsParams,
  PaymentMethodDto,
  PaymentMethodDtoPaginationDto,
  PaymentMethodSetupDto,
  ReactivatePaymentMethodRequestDto,
  RequestConnectedAccountRequestDto,
  StartPaymentMethodSetupRequestDto,
  UpdatePaymentMethodRequestDto,
} from '~/swagger/pay/Api';
import type {
  CreatePaymentMethodForEntityParamsFlexible,
  DeactivatePaymentMethodForEntityParamsFlexible,
  DeactivatePaymentMethodParamsFlexible,
  DeletePaymentMethodForEntityParamsFlexible,
  DeletePaymentMethodParamsFlexible,
  ExchangeAuthorizedMethodsForEntityParamsFlexible,
  GetPaymentMethodForEntityParamsFlexible,
  GetPaymentMethodParamsFlexible,
  GetPaymentMethodsForEntityParamsFlexible,
  ReactivatePaymentMethodForEntityParamsFlexible,
  ReactivatePaymentMethodParamsFlexible,
  StartPaymentMethodSetupForEntityParamsFlexible,
  UpdatePaymentMethodForEntityParamsFlexible,
  UpdatePaymentMethodParamsFlexible,
} from '~/swagger/pay/payParams';

const PAY_METHODS_BASE_URL = `${envConfig.service.pay}/company-portal/methods`;
const PAY_CONNECTED_ACCOUNTS_BASE_URL = `${envConfig.service.pay}/company-portal/accounts`;

function toPathId(v: string | number): string {
  return String(v);
}

export class PayPaymentMethodsRepository extends BaseRestRepository {
  public async getConnectedAccounts(params: { bankAccountIds: string[] }): Promise<ConnectedAccountDtoPaginationDto> {
    if (!params.bankAccountIds.length) {
      return { items: [], totalCount: 0, totalPages: 0, page: 1, pageSize: 0 };
    }
    const url = urlWithQuery(PAY_CONNECTED_ACCOUNTS_BASE_URL, params);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async requestConnectedAccount(body: RequestConnectedAccountRequestDto): Promise<ConnectedAccountDto> {
    const response = await this.fetchWithAuth(PAY_CONNECTED_ACCOUNTS_BASE_URL, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** StartPaymentMethodSetup */
  public async startPaymentMethodSetup(body?: StartPaymentMethodSetupRequestDto): Promise<PaymentMethodSetupDto> {
    const url = `${PAY_METHODS_BASE_URL}/setup`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.getJsonResponse(response);
  }

  /** CreatePaymentMethod */
  public async createPaymentMethod(
    body:
      | CreatePaymentMethodRequestDto
      | CreateTokenPaymentMethodRequestDto
      | CreateCardPaymentMethodRequestDto
      | CreateAchPaymentMethodRequestDto
  ): Promise<PaymentMethodDto> {
    const url = PAY_METHODS_BASE_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** GetPaymentMethods */
  public async getPaymentMethods(params?: GetPaymentMethodsParams): Promise<PaymentMethodDtoPaginationDto> {
    const url = urlWithQuery(PAY_METHODS_BASE_URL, params);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  /** ExchangeAuthorizedMethods */
  public async exchangeAuthorizedMethods(body: ExchangeAuthorizedMethodsRequestDto): Promise<ExchangedAuthorizedMethodsDto> {
    const url = `${PAY_METHODS_BASE_URL}/exchange`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** GetPaymentMethod */
  public async getPaymentMethod(params: GetPaymentMethodParamsFlexible): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${toPathId(params.methodId)}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  /** UpdatePaymentMethod */
  public async updatePaymentMethod(
    params: UpdatePaymentMethodParamsFlexible,
    body: UpdatePaymentMethodRequestDto
  ): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${toPathId(params.methodId)}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** DeletePaymentMethod */
  public async deletePaymentMethod(params: DeletePaymentMethodParamsFlexible, body?: DeletePaymentMethodRequestDto): Promise<void> {
    const url = `${PAY_METHODS_BASE_URL}/${toPathId(params.methodId)}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
      body: JSON.stringify(body ?? {}),
    });
  }

  /** DeactivatePaymentMethod */
  public async deactivatePaymentMethod(
    params: DeactivatePaymentMethodParamsFlexible,
    body: ReactivatePaymentMethodRequestDto
  ): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${toPathId(params.methodId)}/deactivate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** ReactivatePaymentMethod */
  public async reactivatePaymentMethod(
    params: ReactivatePaymentMethodParamsFlexible,
    body?: ReactivatePaymentMethodRequestDto
  ): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${toPathId(params.methodId)}/reactivate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.getJsonResponse(response);
  }

  /** StartPaymentMethodSetupForEntity */
  public async startPaymentMethodSetupForEntity(
    params: StartPaymentMethodSetupForEntityParamsFlexible,
    body?: StartPaymentMethodSetupRequestDto
  ): Promise<PaymentMethodSetupDto> {
    const url = `${PAY_METHODS_BASE_URL}/${params.entityType}/${toPathId(params.entityId)}/setup`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.getJsonResponse(response);
  }

  /** CreatePaymentMethodForEntity */
  public async createPaymentMethodForEntity(
    params: CreatePaymentMethodForEntityParamsFlexible,
    body:
      | CreatePaymentMethodRequestDto
      | CreateTokenPaymentMethodRequestDto
      | CreateCardPaymentMethodRequestDto
      | CreateAchPaymentMethodRequestDto
  ): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${params.entityType}/${toPathId(params.entityId)}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** GetPaymentMethodsForEntity */
  public async getPaymentMethodsForEntity(params: GetPaymentMethodsForEntityParamsFlexible): Promise<PaymentMethodDtoPaginationDto> {
    const { entityType, entityId, ...query } = params;
    const baseUrl = `${PAY_METHODS_BASE_URL}/${entityType}/${toPathId(entityId)}`;
    const url = urlWithQuery(baseUrl, query);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  /** ExchangeAuthorizedMethodsForEntity */
  public async exchangeAuthorizedMethodsForEntity(
    params: ExchangeAuthorizedMethodsForEntityParamsFlexible,
    body: ExchangeAuthorizedMethodsRequestDto
  ): Promise<ExchangedAuthorizedMethodsDto> {
    const url = `${PAY_METHODS_BASE_URL}/${params.entityType}/${toPathId(params.entityId)}/exchange`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** GetPaymentMethodForEntity */
  public async getPaymentMethodForEntity(params: GetPaymentMethodForEntityParamsFlexible): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${params.entityType}/${toPathId(params.entityId)}/${toPathId(params.methodId)}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  /** UpdatePaymentMethodForEntity */
  public async updatePaymentMethodForEntity(
    params: UpdatePaymentMethodForEntityParamsFlexible,
    body: UpdatePaymentMethodRequestDto
  ): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${params.entityType}/${toPathId(params.entityId)}/${toPathId(params.methodId)}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** DeletePaymentMethodForEntity */
  public async deletePaymentMethodForEntity(
    params: DeletePaymentMethodForEntityParamsFlexible,
    body?: DeletePaymentMethodRequestDto
  ): Promise<void> {
    const url = `${PAY_METHODS_BASE_URL}/${params.entityType}/${toPathId(params.entityId)}/${toPathId(params.methodId)}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
      body: JSON.stringify(body ?? {}),
    });
  }

  /** DeactivatePaymentMethodForEntity */
  public async deactivatePaymentMethodForEntity(
    params: DeactivatePaymentMethodForEntityParamsFlexible,
    body: ReactivatePaymentMethodRequestDto
  ): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${params.entityType}/${toPathId(params.entityId)}/${toPathId(params.methodId)}/deactivate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.getJsonResponse(response);
  }

  /** ReactivatePaymentMethodForEntity */
  public async reactivatePaymentMethodForEntity(
    params: ReactivatePaymentMethodForEntityParamsFlexible,
    body?: ReactivatePaymentMethodRequestDto
  ): Promise<PaymentMethodDto> {
    const url = `${PAY_METHODS_BASE_URL}/${params.entityType}/${toPathId(params.entityId)}/${toPathId(params.methodId)}/reactivate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.getJsonResponse(response);
  }
}

export const payPaymentMethodsRepository = new PayPaymentMethodsRepository();
